/**
 * The Design Builder's BUTTON widget: a styled button with ONE feature
 * (download / call API / filter / navigate / popup).
 *
 * Presentational only. The click is emitted and the host's runtime does the
 * work (HTTP, filters, routing), the same split the other input controls use.
 * The one exception is the popup: it is opened here, by an embedded
 * `app-dvm-popup` with its own trigger hidden, so it looks and behaves exactly
 * like the Popup widget.
 */

import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, Output, ViewChild, inject } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';

import { DvmPopupComponent } from '../../chart-components/dvm-popup/dvm-popup.component';
import { buttonConfigOf, opensPopupScreen, type ButtonConfig } from './dvm-action-button.types';

/** How long a result line ("Saved 1 row", "Request failed") stays under the button. */
const MESSAGE_VISIBLE_MS = 5000;

@Component({
  selector: 'app-dvm-action-button',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, MatIconModule, DvmPopupComponent],
  templateUrl: './dvm-action-button.component.html',
  styleUrl: './dvm-action-button.component.scss',
})
export class DvmActionButtonComponent implements OnDestroy {
  config: ButtonConfig = buttonConfigOf(null);

  /** The widget's resolved data: every `ButtonConfig` key at the top level. */
  @Input() set data(value: Record<string, unknown> | null | undefined) {
    this.config = buttonConfigOf(value);
    this.popupData = this.buildPopupData();
  }
  /** The action is running (API call, download, filter recompute). */
  @Input() busy = false;
  /** The action is currently invalid for its configured input values. */
  @Input() disabled = false;
  /** A toggle filter is currently applied. */
  @Input() active = false;
  /** Short result line under the button ("Downloaded", "Request failed"); shown for a few seconds. */
  @Input() set message(value: string | null | undefined) {
    clearTimeout(this.messageTimer);
    this.shownMessage = value ?? '';
    if (this.shownMessage) {
      this.messageTimer = setTimeout(() => {
        this.shownMessage = '';
        this.cdr.markForCheck();
      }, MESSAGE_VISIBLE_MS);
    }
  }
  /** The result line currently on screen ('' once it has faded). */
  shownMessage = '';
  private messageTimer?: ReturnType<typeof setTimeout>;
  private readonly cdr = inject(ChangeDetectorRef);
  @Input() messageKind: 'ok' | 'error' | '' = '';
  /** Content of the POPUP widget picked as the source (popupSource = widget). */
  @Input() set popupWidgetData(value: Record<string, unknown> | null | undefined) {
    this.popupWidget = value ?? null;
    this.popupData = this.buildPopupData();
  }
  private popupWidget: Record<string, unknown> | null = null;

  /**
   * What the embedded popup renders. Rebuilt only when an input changes: a new
   * object on every check would make the popup drop its fetched API blocks.
   */
  popupData: Record<string, unknown> = {};

  /** Clicked (and confirmed) — the host runs the action. Not emitted for embedded popups. */
  @Output() pressed = new EventEmitter<void>();
  /** A `navigate` button inside the popup was clicked. */
  @Output() navigateTo = new EventEmitter<string>();

  @ViewChild(DvmPopupComponent) private popup?: DvmPopupComponent;

  get buttonStyles(): Record<string, string> {
    const c = this.config;
    const justify = c.labelAlign === 'left' ? 'flex-start' : c.labelAlign === 'right' ? 'flex-end' : 'center';
    return {
      '--dab-bg': c.backgroundColor,
      '--dab-bg-hover': c.hoverBackgroundColor || c.backgroundColor,
      '--dab-color': c.textColor,
      '--dab-border': c.borderColor,
      'border-width': `${Math.max(0, Number(c.borderWidth) || 0)}px`,
      'border-radius': `${Math.max(0, Number(c.borderRadius) || 0)}px`,
      'font-family': c.fontFamily,
      'font-size': `${Number(c.fontSize) || 14}px`,
      'font-weight': String(c.fontWeight || 600),
      'justify-content': justify,
      'text-align': c.labelAlign,
      width: c.fullWidth ? '100%' : 'auto',
    };
  }

  /** Title/message or Popup-widget content, rendered by the embedded popup. */
  get hasEmbeddedPopup(): boolean {
    return this.config.action === 'popup' && !opensPopupScreen(this.config);
  }

  get showIcon(): boolean {
    return !!this.config.icon.trim();
  }

  /** The picked Popup widget's content, or the inline, fully styled box. */
  private buildPopupData(): Record<string, unknown> {
    const c = this.config;
    if (c.popupSource === 'widget' && this.popupWidget) return this.popupWidget;
    const align = c.popupTextAlign;
    const heading = c.popupHeading.trim();
    const square = c.popupSize === 'square' ? Math.max(120, Number(c.popupBoxSize) || 360) : 0;
    return {
      title: c.popupTitle,
      popupSize: c.popupSize,
      popupPosition: c.popupPosition,
      closeLabel: c.popupCloseLabel,
      fontFamily: c.fontFamily,
      fontSize: Number(c.popupFontSize) || 14,
      fontWeight: Number(c.popupFontWeight) || 400,
      boxWidth: square,
      boxHeight: square,
      showHeader: c.popupShowHeader,
      bodyAlign: c.popupVerticalAlign,
      bodyBg: c.popupBackgroundColor,
      bodyText: c.popupTextColor,
      headerBg: c.popupHeaderBackground,
      headerText: c.popupHeaderTextColor,
      borderColor: c.popupBorderColor,
      borderWidth: Math.max(0, Number(c.popupBorderWidth) || 0),
      popupRadius: Math.max(0, Number(c.popupBorderRadius) || 0),
      closeBg: c.popupCloseBackground,
      closeText: c.popupCloseTextColor,
      blocks: [
        ...(heading ? [{ type: 'heading', text: heading, level: c.popupHeadingSize, align }] : []),
        ...(c.popupMessage.trim()
          ? c.popupMessage.split(/\n{2,}/).map((text) => ({ type: 'text', text, align }))
          : []),
      ],
    };
  }

  onClick(): void {
    if (this.busy) return;
    const confirmMessage = this.config.confirmMessage.trim();
    if (confirmMessage && !window.confirm(confirmMessage)) return;
    if (this.config.action === 'popup' && !opensPopupScreen(this.config)) {
      this.popup?.openPopup();
      return;
    }
    this.pressed.emit();
  }

  ngOnDestroy(): void {
    clearTimeout(this.messageTimer);
  }
}
